// Port de Leyro Header.jsx adaptado a chain.
//
// Cambios vs Leyro:
//   • Logo chain (SVG inline para non depender dun /logo.png externo).
//   • Sen Axudas chip (non aplica), sen NotificationsPanel (V2).
//   • Search pill simple — leva a /marketplace co query como param.
//   • Selector de idioma só con gl/es (Header de Leyro tiña pt+en como
//     "coming soon"; en chain ocultámolos por agora).
//   • Menú de usuario simplificado: Perfil · As miñas compras · Saír,
//     ou Entrar/Crear conta cando non hai sesión.
//   • Mantén o sticky + shadow ao scrollar, e o estilo Airbnb.

import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import { Bell, Menu as MenuIcon, Search } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import { getUnreadCount } from '../../api/conversations';
import {
  listNotifications,
  markAllNotificationsRead,
  markNotificationRead,
  type NotificationItem,
} from '../../api/notifications';
import { useAuthContext } from '../../context/AuthContext';

import Avatar from '../ui/Avatar';
import LogoMark from '../ui/LogoMark';

const UNREAD_POLL_MS = 30_000;

export interface HeaderProps {
  onOpenAuth: (mode: 'login' | 'register') => void;
}

export default function Header({ onOpenAuth }: HeaderProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, logout } = useAuthContext();
  const [searchText, setSearchText] = useState('');
  const [unread, setUnread] = useState(0);
  // Só os gandeiros poden vender animais — a operación ocorre na app
  // móbil (asinada coa identidade do usuario sobre cada animal real).
  // Para o resto de roles ocultamos a CTA para non confundir.
  const canSell = !!user?.roles.includes('FARMER');
  // Lote V-13 — Admin entry só visible para SILLEDA_ADMIN (multi-rol aware).
  // O panel `/marketplace/admin` ten o seu propio gate de 2FA step-up; aquí
  // só decidimos se ensinar a entrada no menú.
  const isAdmin = !!user?.roles.includes('SILLEDA_ADMIN');

  useEffect(() => {
    if (!user) {
      setUnread(0);
      return;
    }
    let cancelled = false;
    const tick = () => {
      void getUnreadCount()
        .then((r) => {
          if (!cancelled) setUnread(r.unread);
        })
        .catch(() => undefined);
    };
    tick();
    const t = window.setInterval(tick, UNREAD_POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(t);
    };
  }, [user]);

  function handleSearch(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const q = searchText.trim();
    navigate(q ? `/marketplace?q=${encodeURIComponent(q)}` : '/marketplace');
  }

  return (
    <header className="sticky top-0 z-50 bg-white shadow-header">
      <div className="max-w-[1760px] mx-auto px-4 sm:px-6 h-[72px] flex items-center justify-between gap-2 sm:gap-4">
        {/* Logo — mesma marca que a landing chain.leyro.es:
            icona wheat+blockchain + "LEYROCHAIN" wordmark + "Traceability" tag. */}
        <div className="flex-shrink-0">
          <Link
            to="/marketplace"
            aria-label="LeyroChain — Inicio"
            className="flex items-center gap-2"
          >
            <LogoMark size={32} />
            <span className="hidden sm:flex items-baseline gap-1.5 leading-none">
              <span className="font-extrabold text-dark tracking-wider text-base">LEYROCHAIN</span>
              <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#a8895a]">
                Traceability
              </span>
            </span>
          </Link>
        </div>

        {/* Search pill */}
        <form onSubmit={handleSearch} className="hidden md:flex flex-1 justify-center max-w-2xl">
          <div className="search-pill flex-1 max-w-xl px-5 py-2 gap-3">
            <Search size={16} className="text-rauxa shrink-0" />
            <input
              type="text"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder={t('header.searchPlaceholder')}
              className="flex-1 outline-none text-sm text-dark placeholder-muted bg-transparent"
            />
            <button
              type="submit"
              className="shrink-0 rounded-full bg-rauxa text-white text-xs font-semibold px-3 py-1.5 hover:opacity-90"
            >
              {t('header.searchShort')}
            </button>
          </div>
        </form>

        {/* Mobile search button */}
        <button
          type="button"
          className="flex md:hidden items-center gap-1.5 border border-border rounded-full px-3 py-1.5 shadow-card text-sm text-dark min-w-0"
          onClick={() => navigate('/marketplace')}
          aria-label={t('header.searchShort')}
        >
          <Search size={14} className="text-rauxa shrink-0" />
          <span className="font-medium truncate">{t('header.searchShort')}</span>
        </button>

        {/* Right controls */}
        <div className="flex-shrink-0 flex items-center gap-1">
          {/* "Vender un animal" CTA — só desktop + só gandeiros */}
          {canSell && (
            <button
              type="button"
              onClick={() => navigate('/marketplace/vender')}
              className="hidden lg:block rounded-full px-4 py-2 text-sm font-semibold bg-dark text-white hover:bg-black transition-colors whitespace-nowrap"
            >
              {t('header.sellCta')}
            </button>
          )}

          {/* Notifications bell (stub UI — Lote UX Leyro parity iter-2).
              Sen endpoint backend aínda: badge sempre 0, panel mostra
              empty state friendly. Cando exista GET /notifications,
              substituír por un fetch + render real. */}
          {user && <NotificationsBell />}

          {/* User menu */}
          <Menu as="div" className="relative">
            <MenuButton
              className="flex items-center gap-2 border border-border rounded-full p-1 pl-3 hover:shadow-card transition-shadow"
              aria-label={t('header.menuAria')}
            >
              <MenuIcon size={16} className="text-dark" />
              <div className="w-8 h-8 rounded-full overflow-hidden bg-muted flex items-center justify-center">
                {user ? (
                  <Avatar user={user} size={32} />
                ) : (
                  <span className="text-white text-xs font-semibold select-none">?</span>
                )}
              </div>
            </MenuButton>

            <MenuItems
              className="absolute right-0 top-full mt-2 w-60 bg-white rounded-2xl shadow-card-hover border border-border py-2 z-50 animate-fade-in focus:outline-none"
              anchor="bottom end"
            >
              {user ? (
                <>
                  <div className="flex items-center gap-3 px-4 py-3 border-b border-border mb-1">
                    <Avatar user={user} size={36} />
                    <div className="flex flex-col min-w-0">
                      <span className="text-sm font-semibold text-dark truncate">
                        {user.name || user.email}
                      </span>
                      <span className="text-xs text-muted truncate">{user.email}</span>
                    </div>
                  </div>

                  <MenuItem>
                    {({ focus }) => (
                      <button
                        className={`flex w-full px-4 py-3 text-sm text-dark ${
                          focus ? 'bg-subtle' : ''
                        }`}
                        onClick={() => navigate('/marketplace/profile')}
                      >
                        {t('header.menu.profile')}
                      </button>
                    )}
                  </MenuItem>
                  <MenuItem>
                    {({ focus }) => (
                      <button
                        className={`flex w-full px-4 py-3 text-sm text-dark ${
                          focus ? 'bg-subtle' : ''
                        }`}
                        onClick={() => navigate('/marketplace/me/purchases')}
                      >
                        {t('header.menu.purchases')}
                      </button>
                    )}
                  </MenuItem>
                  <MenuItem>
                    {({ focus }) => (
                      <button
                        className={`flex w-full px-4 py-3 text-sm text-dark items-center justify-between ${
                          focus ? 'bg-subtle' : ''
                        }`}
                        onClick={() => navigate('/marketplace/messages')}
                      >
                        <span>{t('header.menu.messages')}</span>
                        {unread > 0 && (
                          <span className="text-[10px] font-bold text-white bg-rauxa rounded-full px-1.5 py-0.5 min-w-[18px] text-center">
                            {unread > 99 ? '99+' : unread}
                          </span>
                        )}
                      </button>
                    )}
                  </MenuItem>
                  <MenuItem>
                    {({ focus }) => (
                      <button
                        className={`flex w-full px-4 py-3 text-sm text-dark ${
                          focus ? 'bg-subtle' : ''
                        }`}
                        onClick={() => navigate('/marketplace/kyc')}
                      >
                        {t('header.menu.kyc')}
                      </button>
                    )}
                  </MenuItem>
                  <MenuItem>
                    {({ focus }) => (
                      <button
                        className={`flex w-full px-4 py-3 text-sm text-dark ${
                          focus ? 'bg-subtle' : ''
                        }`}
                        onClick={() => navigate('/marketplace/profile/roles')}
                      >
                        {t('header.menu.roles')}
                      </button>
                    )}
                  </MenuItem>
                  <MenuItem>
                    {({ focus }) => (
                      <button
                        className={`flex w-full px-4 py-3 text-sm text-dark ${
                          focus ? 'bg-subtle' : ''
                        }`}
                        onClick={() => navigate('/marketplace/shop')}
                      >
                        {t('header.menu.shop')}
                      </button>
                    )}
                  </MenuItem>
                  <MenuItem>
                    {({ focus }) => (
                      <button
                        className={`flex w-full px-4 py-3 text-sm text-dark ${
                          focus ? 'bg-subtle' : ''
                        }`}
                        onClick={() => navigate('/marketplace/facturas')}
                      >
                        {t('header.menu.invoices')}
                      </button>
                    )}
                  </MenuItem>
                  {canSell && (
                    <MenuItem>
                      {({ focus }) => (
                        <button
                          className={`flex w-full px-4 py-3 text-sm text-dark ${
                            focus ? 'bg-subtle' : ''
                          }`}
                          onClick={() => navigate('/marketplace/vender')}
                        >
                          {t('header.menu.sell')}
                        </button>
                      )}
                    </MenuItem>
                  )}
                  {/* Lote V-13 — admin entry. O panel ten o seu propio 2FA
                      step-up; o item só sinaliza que a sección existe. */}
                  {isAdmin && (
                    <>
                      <div className="border-t border-border my-1" />
                      <MenuItem>
                        {({ focus }) => (
                          <button
                            className={`flex w-full px-4 py-3 text-sm font-semibold text-rauxa ${
                              focus ? 'bg-subtle' : ''
                            }`}
                            onClick={() => navigate('/marketplace/admin')}
                          >
                            {t('header.menu.admin')}
                          </button>
                        )}
                      </MenuItem>
                    </>
                  )}

                  <div className="border-t border-border my-1" />

                  <MenuItem>
                    {({ focus }) => (
                      <button
                        className={`flex w-full px-4 py-3 text-sm text-dark ${
                          focus ? 'bg-subtle' : ''
                        }`}
                        onClick={() => {
                          void logout();
                          navigate('/marketplace');
                        }}
                      >
                        {t('header.menu.logout')}
                      </button>
                    )}
                  </MenuItem>
                </>
              ) : (
                <>
                  <MenuItem>
                    {({ focus }) => (
                      <button
                        className={`flex w-full px-4 py-3 text-sm font-semibold text-dark ${
                          focus ? 'bg-subtle' : ''
                        }`}
                        onClick={() => onOpenAuth('login')}
                      >
                        {t('header.menu.signin')}
                      </button>
                    )}
                  </MenuItem>
                  <MenuItem>
                    {({ focus }) => (
                      <button
                        className={`flex w-full px-4 py-3 text-sm text-dark ${
                          focus ? 'bg-subtle' : ''
                        }`}
                        onClick={() => onOpenAuth('register')}
                      >
                        {t('header.menu.register')}
                      </button>
                    )}
                  </MenuItem>
                </>
              )}
            </MenuItems>
          </Menu>
        </div>
      </div>
    </header>
  );
}

// ─── Notifications bell ────────────────────────────────────────────────────
// Fetch real: polling cada 30s sobre /api/v1/notifications. Mostra os
// últimos 10 items + badge de unread count. Tocar nun item márcao como
// lido e navega ao deeplink se está dispoñible.

const NOTIFICATIONS_POLL_MS = 30_000;
const MAX_RENDER = 10;

function NotificationsBell(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [items, setItems] = useState<NotificationItem[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const tick = () => {
      void listNotifications(MAX_RENDER)
        .then((r) => {
          if (cancelled) return;
          setItems(r.notifications);
          setUnreadCount(r.unread_count);
        })
        .catch(() => undefined);
    };
    tick();
    const id = window.setInterval(tick, NOTIFICATIONS_POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, []);

  const handleItemClick = (n: NotificationItem) => {
    if (!n.read_at) {
      void markNotificationRead(n.id).catch(() => undefined);
      setItems((cur) =>
        cur.map((x) => (x.id === n.id ? { ...x, read_at: new Date().toISOString() } : x)),
      );
      setUnreadCount((c) => Math.max(0, c - 1));
    }
    // Deep-link convencional: data_json.sale_id → marketplace/sale/<id>
    const data = (n.data_json ?? {}) as Record<string, string>;
    if (typeof data.sale_id === 'string') {
      navigate(`/marketplace/sale/${data.sale_id}`);
    } else if (typeof data.conversation_id === 'string') {
      navigate(`/marketplace/messages/${data.conversation_id}`);
    }
  };

  const handleMarkAll = () => {
    void markAllNotificationsRead()
      .then(() => {
        setItems((cur) =>
          cur.map((x) => (x.read_at ? x : { ...x, read_at: new Date().toISOString() })),
        );
        setUnreadCount(0);
      })
      .catch(() => undefined);
  };

  return (
    <Menu as="div" className="relative">
      <MenuButton
        className="relative w-9 h-9 rounded-full hover:bg-subtle flex items-center justify-center transition-colors"
        aria-label={t('header.notifications.ariaLabel')}
      >
        <Bell size={18} className="text-dark" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 bg-rauxa text-white text-[10px] rounded-full min-w-[18px] h-[18px] flex items-center justify-center font-semibold px-1">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </MenuButton>

      <MenuItems
        anchor="bottom end"
        className="mt-2 w-80 bg-white rounded-2xl shadow-card-hover border border-border z-50 animate-fade-in focus:outline-none overflow-hidden max-h-[480px] overflow-y-auto"
      >
        <div className="px-4 py-3 border-b border-border flex items-center justify-between">
          <h3 className="text-sm font-semibold text-dark">{t('header.notifications.title')}</h3>
          {unreadCount > 0 && (
            <button
              type="button"
              onClick={handleMarkAll}
              className="text-xs text-rauxa hover:underline font-medium"
            >
              {t('header.notifications.markAllRead')}
            </button>
          )}
        </div>
        {items.length === 0 ? (
          <div className="px-4 py-8 flex flex-col items-center text-center gap-2">
            <Bell size={28} className="text-muted" aria-hidden="true" />
            <p className="text-sm font-medium text-dark">{t('header.notifications.emptyTitle')}</p>
            <p className="text-xs text-muted leading-relaxed max-w-[220px]">
              {t('header.notifications.emptyBody')}
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {items.map((n) => (
              <li key={n.id}>
                <MenuItem>
                  {({ focus }) => (
                    <button
                      type="button"
                      onClick={() => handleItemClick(n)}
                      className={`w-full text-left px-4 py-3 ${focus ? 'bg-subtle' : ''} ${
                        !n.read_at ? 'bg-rauxa/5' : ''
                      }`}
                    >
                      <div className="flex items-start gap-2">
                        {!n.read_at && (
                          <span className="mt-1.5 w-2 h-2 rounded-full bg-rauxa flex-none" />
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-dark truncate">{n.title}</p>
                          <p className="text-xs text-muted leading-snug line-clamp-2">{n.text}</p>
                          <p className="text-[10px] text-muted mt-1">
                            {formatRelative(n.created_at)}
                          </p>
                        </div>
                      </div>
                    </button>
                  )}
                </MenuItem>
              </li>
            ))}
          </ul>
        )}
      </MenuItems>
    </Menu>
  );
}

function formatRelative(iso: string): string {
  const then = new Date(iso).getTime();
  const diffSec = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (diffSec < 60) return 'agora';
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `hai ${diffMin} min`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `hai ${diffHr} h`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 7) return `hai ${diffDay} d`;
  return new Date(iso).toLocaleDateString();
}
